import React from 'react';
import { Button } from '../ui/button';
import { AlertTriangle, ArrowRight, RefreshCw, X } from 'lucide-react';

const fmt = (n, d = 0) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
const pctOf = (from, to) => (from ? `${to - from > 0 ? '+' : ''}${(((to - from) / from) * 100).toFixed(1)}%` : '');

/**
 * Shown when a loaded estimate's saved effort/cost no longer matches what
 * today's rate cards / calculator produce for the same inputs.
 */
export function RateDriftBanner({ drift, currencySymbol = '$', onReprice, onDismiss }) {
  if (!drift) return null;
  const costMoved = Math.abs(drift.liveCost - drift.savedCost) >= 0.5;
  const effortMoved = Math.abs(drift.liveEffort - drift.savedEffort) >= 0.05;
  return (
    <div
      className="flex flex-col md:flex-row md:items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-6 shadow-sm"
      role="status"
      data-testid="rate-drift-banner"
    >
      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900">Saved values are out of date with today's rate cards</p>
        <p className="text-xs text-amber-800 mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1">
          {costMoved && (
            <span className="inline-flex items-center gap-1 tabular-nums" data-testid="rate-drift-cost">
              <span className="font-semibold">Cost</span>
              {currencySymbol}{fmt(drift.savedCost)} <ArrowRight className="w-3 h-3" /> <b>{currencySymbol}{fmt(drift.liveCost)}</b>
              <span className="opacity-75">({pctOf(drift.savedCost, drift.liveCost)})</span>
            </span>
          )}
          {effortMoved && (
            <span className="inline-flex items-center gap-1 tabular-nums" data-testid="rate-drift-effort">
              <span className="font-semibold">Effort</span>
              {fmt(drift.savedEffort, 1)}h <ArrowRight className="w-3 h-3" /> <b>{fmt(drift.liveEffort, 1)}h</b>
              <span className="opacity-75">({pctOf(drift.savedEffort, drift.liveEffort)})</span>
            </span>
          )}
          <span className="text-amber-700/80">The saved figures stay in force until you reprice and save.</span>
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" onClick={onReprice} className="h-8 bg-amber-600 hover:bg-amber-700 text-white" data-testid="rate-drift-reprice-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reprice at today's rates
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss} className="h-8 text-amber-800 hover:bg-amber-100" data-testid="rate-drift-dismiss-btn">
          <X className="w-3.5 h-3.5 mr-1" /> Keep saved
        </Button>
      </div>
    </div>
  );
}
